import React, { useContext, useEffect, useState } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import Alert from '@digital-hig/mui/Alert';
import AlertIcon from '@digital-hig/mui/AlertIcon';
import LoadingOverlay from 'react-loading-overlay';

import { useCollectionPaginatedItems } from '../../../apis/collections/hooks';
import { getBookmarksCollectionId } from '../../../apis/collections/utils';
import EntriesTable from '../../../components/container/entries-table';
import { AUDefaultContent } from '../../../components/presentational/AUDefaultContent';
import languageOptions from '../../../helpers/languageOptions';
import I18nContext from '../../contexts/I18nContext';
import LoginContext from '../../contexts/LoginContext';

export interface BookmarkListingProps {
  baseURL?: string;
  langCode?: string;
}

const PAGE_SIZE = 10;

export default function BookmarkListing({
  baseURL,
  langCode,
}: BookmarkListingProps): JSX.Element {
  const { t } = useContext(I18nContext);

  const [currentPage, setCurrentPage] = useState(1);
  const { userProfile, isUserLoading, isLoggedIn } = useContext(LoginContext);
  const { data, isLoading } = useCollectionPaginatedItems({
    baseURL,
    id: getBookmarksCollectionId(),
    enabled: Boolean(userProfile?.sub),
    params: {
      provider: 'aws',
      pageSize: PAGE_SIZE,
      pageNumber: currentPage,
    },
  });

  useEffect(() => {
    const pageNumber = Number(data?.meta?.pageNumber);
    if (pageNumber && currentPage !== pageNumber) {
      setCurrentPage(pageNumber);
    }
  }, [data?.meta?.pageNumber]);

  const totalPages = data?.meta?.pageCount || 0;
  const items = data?.collection_items || [];

  return (
    <div className="au-w-full au-grow">
      <LoadingOverlay
        active={isUserLoading || isLoading}
        spinner
        fadeSpeed={250}
        styles={{
          overlay: (base: object) => ({
            ...base,
            position: 'fixed',
            zIndex: 94035,
          }),
        }}
      />
      {!isUserLoading && !isLoggedIn && (
        <Alert
          icon={<AlertIcon severity="error" variant="outlined" />}
          severity="error"
          variant="outlined"
          className="!au-text-white"
        >
          {t('error_401', { ns: 'common' })}
        </Alert>
      )}
      {!isLoading && !isUserLoading && isLoggedIn && items?.length === 0 && (
        <AUDefaultContent type="bookmarks" />
      )}
      <EntriesTable
        items={items.map(item => ({
          id: item.item_key,
          title: item.item_title,
        }))}
        loading={isLoading || isUserLoading}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
        baseURL={baseURL}
        langCode={langCode}
      />
    </div>
  );
}

// Shared attribute definition for Block Editor and AURR.
BookmarkListing.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userProfileURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  userContentURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  langCode: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
};
